import React, { useState } from 'react';
import type { Table, Order, FloorFixture } from '../types';
import PlusIcon from '../components/icons/PlusIcon';
import TableModal from '../components/tables/TableModal';
import FloorPlanView from '../components/pos/FloorPlanView';
import FloorPlanKonva from '../components/pos/FloorPlanKonva';
import EditIcon from '../components/icons/EditIcon';
import EnhancedLayoutEditor from '../components/tables/EnhancedLayoutEditor';

interface TableManagementPageProps {
  tables: Table[];
  orders: Order[];
  layoutComponents: FloorFixture[];
  onSave: (table: Omit<Table, 'status'>) => void;
  onDelete: (tableId: number) => void;
  onUpdatePosition: (tableId: number, x: number, y: number) => void;
  onUpdateLayoutComponent: (componentId: string, updates: Partial<FloorFixture>) => void;
  onUpdateLayoutComponentPosition: (componentId: string, x: number, y: number) => void;
  onCreateLayoutComponent?: (type: FloorFixture['type']) => void;
  onDeleteLayoutComponent?: (componentId: string) => void;
}

const TableManagementPage: React.FC<TableManagementPageProps> = ({ tables, orders, layoutComponents, onSave, onDelete, onUpdatePosition, onUpdateLayoutComponent, onUpdateLayoutComponentPosition, onCreateLayoutComponent, onDeleteLayoutComponent }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTable, setEditingTable] = useState<Table | null>(null);
  const [isEditLayoutMode, setIsEditLayoutMode] = useState(false);
  const [selectedComponentId, setSelectedComponentId] = useState<string | null>(null);
  const [gridSettings, setGridSettings] = useState({ showGrid: true, snapToGrid: true, gridSize: 5 });

  const handleOpenModal = (table: Table | null = null) => {
    if (isEditLayoutMode) return;
    setEditingTable(table);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingTable(null);
  };

  const handleSave = (table: Omit<Table, 'status'>) => {
    onSave(table);
    handleCloseModal();
  };
  
  const handleDelete = (tableId: number) => {
    if (window.confirm('Are you sure you want to delete this table? This cannot be undone.')) {
      onDelete(tableId);
      handleCloseModal();
    }
  }

  const handleTableClick = (tableId: number) => {
    if (isEditLayoutMode) return;
    const tableToEdit = tables.find(t => t.id === tableId);
    if (tableToEdit) {
        handleOpenModal(tableToEdit);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <header className="bg-white shadow-md z-10 flex-shrink-0">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <h1 className="text-2xl font-bold text-gray-800">Table Management</h1>
            <div className="flex items-center space-x-4">
               <button 
                onClick={() => setIsEditLayoutMode(!isEditLayoutMode)} 
                className={`font-bold py-2 px-4 rounded-lg transition-colors flex items-center ${
                    isEditLayoutMode 
                    ? 'bg-blue-500 text-white hover:bg-blue-600' 
                    : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                }`}
                >
                <EditIcon className="w-5 h-5 mr-2" />
                {isEditLayoutMode ? 'Finish Editing' : 'Edit Layout'}
              </button>
              <button
                onClick={() => handleOpenModal()}
                className="bg-pink-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-pink-600 transition-colors duration-300 flex items-center"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                Add New Table
              </button>
            </div>
          </div>
        </div>
      </header>
      <main className="flex-1 overflow-hidden flex flex-col">
        {isEditLayoutMode ? (
          <div className="flex-1 flex overflow-hidden" style={{ height: '100%', overflow: 'hidden' }}>
              <EnhancedLayoutEditor
                components={layoutComponents}
                selectedComponentId={selectedComponentId}
                onSelectComponent={setSelectedComponentId}
                onChange={onUpdateLayoutComponent}
                onAdd={onCreateLayoutComponent || (() => {})}
                onDelete={onDeleteLayoutComponent || (() => {})}
                onPositionChange={onUpdateLayoutComponentPosition}
                onGridSettingsChange={setGridSettings}
              />
              <div className="flex-1 overflow-auto" style={{ minWidth: 0 }}>
                <FloorPlanKonva
                  tables={tables}
                  orders={orders}
                  layoutComponents={layoutComponents}
                  onTableClick={handleTableClick}
                  isEditMode={isEditLayoutMode}
                  onTableMove={onUpdatePosition}
                  onLayoutComponentMove={onUpdateLayoutComponentPosition}
                  selectedComponentId={selectedComponentId}
                  onSelectComponent={setSelectedComponentId}
                  showGrid={gridSettings.showGrid}
                  snapToGrid={gridSettings.snapToGrid}
                  gridSize={gridSettings.gridSize}
                />
              </div>
            </div>
        ) : (
          <div className="flex-1 overflow-auto">
            <FloorPlanKonva
              tables={tables}
              orders={orders}
              layoutComponents={layoutComponents}
              onTableClick={handleTableClick}
              isEditMode={isEditLayoutMode}
              onTableMove={onUpdatePosition}
              onLayoutComponentMove={onUpdateLayoutComponentPosition}
            />
          </div>
        )}
      </main>
      {isModalOpen && (
        <TableModal
          table={editingTable}
          onClose={handleCloseModal}
          onSave={handleSave}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};

export default TableManagementPage;